import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { decode } from 'html-entities';
import { ContractData, fixMalformedEntities } from '../../utils/contractUtils';
import CloseButton from './CloseButton';

interface ContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractData: ContractData | null;
  isLoading?: boolean;
}

const ContractModal: React.FC<ContractModalProps> = ({ isOpen, onClose, contractData, isLoading = false }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle Escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50"
      onClick={onClose}>
      <div
        className="bg-white p-6 rounded-md w-11/12 max-w-4xl max-h-[80vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}>
        {/* Close button (X) */}
        <CloseButton onClick={onClose} className="absolute top-4 right-4" />

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-fpcred">Caricamento...</div>
          </div>
        )}

        {/* Contract content */}
        {!isLoading && contractData && (
          <>
            {/* Title */}
            {contractData.title && (
              <h2 className="text-2xl font-bold mb-4 text-fpcred pr-14">{contractData.title}</h2>
            )}

            {/* Disclaimer (if present) */}
            {contractData.disclaimer && (
              <div className="mb-4 p-4 bg-gray-50 rounded">
                <h3 className="font-semibold text-fpcred mb-2">Attenzione:</h3>
                <ReactMarkdown>{decode(fixMalformedEntities(contractData.disclaimer))}</ReactMarkdown>
              </div>
            )}

            {/* Acceptance text (if present) */}
            {contractData.acceptance && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Accettazione</h3>
                <ReactMarkdown>{decode(fixMalformedEntities(contractData.acceptance))}</ReactMarkdown>
              </div>
            )}

            {/* Sections */}
            {contractData.sections?.map((section: any, idx: number) => {
              const sectionNumber = idx + 1;
              return (
                <div key={idx} className="mb-4">
                  <h3 className="font-bold mb-1">
                    {sectionNumber}. {section.title}
                  </h3>
                  {section.content && (
                    <ReactMarkdown>{decode(fixMalformedEntities(section.content))}</ReactMarkdown>
                  )}

                  {/* Subsections */}
                  {section.subsections?.map((sub: any, sIdx: number) => {
                    const subsectionNumber = `${sectionNumber}.${sIdx + 1}`;
                    return (
                      <div key={sIdx} className="ml-4 mt-2 border-l-2 border-fpcpink pl-3">
                        {sub.title && (
                          <h4 className="font-semibold text-sm mb-1">
                            {subsectionNumber}. {sub.title}
                          </h4>
                        )}
                        {sub.content && (
                          <ReactMarkdown>{decode(fixMalformedEntities(sub.content))}</ReactMarkdown>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Close button at bottom */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={onClose}
                className="bg-fpcred text-white font-bold py-2 px-6 rounded-md hover:bg-fpcred/90 transition-colors">
                Chiudi
              </button>
            </div>
          </>
        )}

        {/* Error state - no contract data */}
        {!isLoading && !contractData && (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Impossibile caricare il contratto</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractModal;
